/**
 * `[` moves one level closer, `]` one level wider — the same two moves as the
 * ‹ › arrows on the rail, named in their tooltips (figure S1). Ignored while
 * the viewer is typing, and ignored when the page has no target at that end
 * (the closest and widest views have nowhere further to go).
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { SpineSpec } from './SpineContext'

export const ZOOM_IN_KEY = '['
export const ZOOM_OUT_KEY = ']'

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || !el.tagName) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable === true
}

export function useZoomShortcuts(spec: SpineSpec | null): void {
  const navigate = useNavigate()
  const zoomInTo = spec?.zoomIn?.to
  const zoomOutTo = spec?.zoomOut?.to

  useEffect(() => {
    if (!zoomInTo && !zoomOutTo) return undefined
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      if (e.key === ZOOM_IN_KEY && zoomInTo) {
        e.preventDefault()
        navigate(zoomInTo)
      } else if (e.key === ZOOM_OUT_KEY && zoomOutTo) {
        e.preventDefault()
        navigate(zoomOutTo)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [zoomInTo, zoomOutTo, navigate])
}
